import type { ReactNode } from 'react';
import type { ClinicPhysicianResponse } from '../../services/api/types.gen';
import { PhoneLink } from '../PhoneLink';
import { formatPhysicianName } from '../../utils/formatting';
import { formatOrderDeliveryPreference } from '../../utils/orderDeliveryPreference';

/**
 * The physician metadata a clinician needs to confirm they picked the right
 * person, and to know how that physician's orders will reach them.
 *
 * A name and an NPI alone do not tell two similarly named physicians apart,
 * so the contact details and the order delivery preference travel with the
 * name wherever a physician is offered as a choice.
 */
export function PhysicianDetails({
  physician,
}: {
  physician: ClinicPhysicianResponse;
}) {
  return (
    <div className='space-y-2 text-sm'>
      <div className='font-semibold text-foreground'>
        {formatPhysicianName(physician)}
      </div>
      <dl className='grid grid-cols-[auto_1fr] gap-x-3 gap-y-1'>
        <Row label='NPI'>
          {physician.npi ? (
            <span className='font-mono'>{physician.npi}</span>
          ) : null}
        </Row>
        <Row label='Orders via'>
          {formatOrderDeliveryPreference(physician.order_delivery_preference)}
        </Row>
        <Row label='Phone'>
          {physician.phone ? <PhoneLink phone={physician.phone} /> : null}
        </Row>
        <Row label='Fax'>{physician.fax}</Row>
        <Row label='Email'>{physician.email}</Row>
        <Row label='Address'>{physician.address}</Row>
        <Row label='PECOS'>
          {physician.pecos == null ? null : physician.pecos ? 'Yes' : 'No'}
        </Row>
        <Row label='Note'>{physician.comment}</Row>
      </dl>
    </div>
  );
}

/**
 * One metadata row, dropped entirely when the physician has no value for it.
 * An "Email —" row says nothing that a missing row does not, and the details
 * are easier to scan when only what is known is in them.
 */
function Row({ label, children }: { label: string; children?: ReactNode }) {
  if (children == null || children === '' || children === false) return null;
  return (
    <>
      <dt className='text-muted-foreground'>{label}</dt>
      <dd className='min-w-0 break-words text-foreground'>{children}</dd>
    </>
  );
}
